import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { cn } from '../../utils/cn';
import { useAnimationStore } from '../../store/animationStore';
import { generateEasingCurve } from '../../utils/easing';
import {
  EasingType,
  BezierHandle,
  Keyframe,
  PropertyTrack,
} from '../../../core/types/animation.types';

interface CurveCanvasProps {
  width: number;
  height: number;
  showGrid: boolean;
  selectedKeyframe: Keyframe | null;
  selectedTrack: PropertyTrack | null;
  animationId: string | null;
}

const PADDING = 40;
const HANDLE_RADIUS = 8;
const CONTROL_LINE_COLOR = '#6b7280';
const CURVE_COLOR = '#3b82f6';
const LINEAR_REF_COLOR = '#4b5563';
const GRID_COLOR = '#27272a';
const GRID_LABEL_COLOR = '#71717a';
const AXIS_LABEL_COLOR = '#a1a1aa';

export const CurveCanvas: React.FC<CurveCanvasProps> = ({
  width,
  height,
  showGrid,
  selectedKeyframe,
  selectedTrack,
  animationId,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [draggingHandle, setDraggingHandle] = useState<'in' | 'out' | null>(null);
  const [localHandles, setLocalHandles] = useState<{ in: BezierHandle; out: BezierHandle } | null>(null);

  const { setKeyframeBezier } = useAnimationStore();

  // Default bezier handles for custom easing
  const defaultHandles: { in: BezierHandle; out: BezierHandle } = useMemo(() => ({
    in: { x: 0.6, y: 1 },
    out: { x: 0.4, y: 0 },
  }), []);

  // Get current handles
  const handles = useMemo(() => {
    if (localHandles) return localHandles;
    if (selectedKeyframe?.easing === 'custom' && selectedKeyframe.bezierHandles) {
      return selectedKeyframe.bezierHandles;
    }
    return defaultHandles;
  }, [selectedKeyframe, localHandles, defaultHandles]);

  // Convert normalized coordinates to canvas coordinates
  const toCanvasX = useCallback((normX: number) => {
    return PADDING + normX * (width - 2 * PADDING);
  }, [width]);

  const toCanvasY = useCallback((normY: number) => {
    // Invert Y since canvas Y increases downward
    // Allow for some overshoot space (clamp display range to -0.2 to 1.2)
    const displayRange = 1.4;
    const minY = -0.2;
    return PADDING + (1 - (normY - minY) / displayRange) * (height - 2 * PADDING);
  }, [height]);

  // Convert canvas coordinates to normalized coordinates
  const toNormX = useCallback((canvasX: number) => {
    return (canvasX - PADDING) / (width - 2 * PADDING);
  }, [width]);

  const toNormY = useCallback((canvasY: number) => {
    const displayRange = 1.4;
    const minY = -0.2;
    return (1 - (canvasY - PADDING) / (height - 2 * PADDING)) * displayRange + minY;
  }, [height]);

  // Draw the canvas
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw grid
    if (showGrid) {
      ctx.strokeStyle = GRID_COLOR;
      ctx.lineWidth = 1;

      // Vertical grid lines
      for (let i = 0; i <= 10; i++) {
        const x = toCanvasX(i / 10);
        ctx.beginPath();
        ctx.moveTo(x, PADDING);
        ctx.lineTo(x, height - PADDING);
        ctx.stroke();
      }

      // Horizontal grid lines
      for (let i = 0; i <= 10; i++) {
        const y = toCanvasY(i / 10);
        ctx.beginPath();
        ctx.moveTo(PADDING, y);
        ctx.lineTo(width - PADDING, y);
        ctx.stroke();
      }

      // Grid labels
      ctx.fillStyle = GRID_LABEL_COLOR;
      ctx.font = '10px system-ui, sans-serif';
      ctx.textAlign = 'center';

      // X-axis labels (Time)
      for (let i = 0; i <= 4; i++) {
        const val = i / 4;
        const x = toCanvasX(val);
        ctx.fillText(val.toFixed(1), x, height - PADDING + 15);
      }

      // Y-axis labels (Value)
      ctx.textAlign = 'right';
      for (let i = 0; i <= 4; i++) {
        const val = i / 4;
        const y = toCanvasY(val);
        ctx.fillText(val.toFixed(1), PADDING - 8, y + 3);
      }
    }

    // Draw axis border
    ctx.strokeStyle = '#52525b';
    ctx.lineWidth = 1;
    ctx.strokeRect(PADDING, PADDING, width - 2 * PADDING, height - 2 * PADDING);

    // Draw axis labels
    ctx.fillStyle = AXIS_LABEL_COLOR;
    ctx.font = '11px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Time', width / 2, height - 5);

    ctx.save();
    ctx.translate(12, height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Value', 0, 0);
    ctx.restore();

    // Draw linear reference line (diagonal)
    ctx.beginPath();
    ctx.strokeStyle = LINEAR_REF_COLOR;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.moveTo(toCanvasX(0), toCanvasY(0));
    ctx.lineTo(toCanvasX(1), toCanvasY(1));
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw the easing curve
    if (selectedKeyframe) {
      const easing: EasingType = selectedKeyframe.easing;
      const bezierHandles = selectedKeyframe.easing === 'custom' ? handles : undefined;
      const curvePoints = generateEasingCurve(easing, bezierHandles, 100);

      ctx.beginPath();
      ctx.strokeStyle = CURVE_COLOR;
      ctx.lineWidth = 2;

      curvePoints.forEach((point, index) => {
        const x = toCanvasX(point.x);
        const y = toCanvasY(point.y);
        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.stroke();

      // Draw start and end point markers
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(toCanvasX(0), toCanvasY(0), 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(toCanvasX(1), toCanvasY(1), 4, 0, Math.PI * 2);
      ctx.fill();

      // Draw control points and handles for custom bezier
      if (selectedKeyframe.easing === 'custom' || localHandles) {
        const currentHandles = handles;

        // Draw control lines
        ctx.strokeStyle = CONTROL_LINE_COLOR;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);

        // Line from start point (0, 0) to out handle
        ctx.beginPath();
        ctx.moveTo(toCanvasX(0), toCanvasY(0));
        ctx.lineTo(toCanvasX(currentHandles.out.x), toCanvasY(currentHandles.out.y));
        ctx.stroke();

        // Line from end point (1, 1) to in handle
        ctx.beginPath();
        ctx.moveTo(toCanvasX(1), toCanvasY(1));
        ctx.lineTo(toCanvasX(currentHandles.in.x), toCanvasY(currentHandles.in.y));
        ctx.stroke();
        ctx.setLineDash([]);

        // Draw out handle (P1)
        ctx.fillStyle = draggingHandle === 'out' ? '#fbbf24' : '#22c55e';
        ctx.beginPath();
        ctx.arc(
          toCanvasX(currentHandles.out.x),
          toCanvasY(currentHandles.out.y),
          HANDLE_RADIUS,
          0,
          Math.PI * 2
        );
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Draw in handle (P2)
        ctx.fillStyle = draggingHandle === 'in' ? '#fbbf24' : '#ef4444';
        ctx.beginPath();
        ctx.arc(
          toCanvasX(currentHandles.in.x),
          toCanvasY(currentHandles.in.y),
          HANDLE_RADIUS,
          0,
          Math.PI * 2
        );
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    } else {
      // No keyframe selected - show placeholder text
      ctx.fillStyle = GRID_LABEL_COLOR;
      ctx.font = '12px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Select a keyframe to edit its easing curve', width / 2, height / 2);
    }
  }, [width, height, showGrid, selectedKeyframe, handles, localHandles, toCanvasX, toCanvasY, draggingHandle]);

  // Redraw on changes
  useEffect(() => {
    draw();
  }, [draw]);

  // Handle mouse events for dragging bezier handles
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!selectedKeyframe || selectedKeyframe.easing !== 'custom') return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const currentHandles = handles;

    // Check if clicking on out handle
    const outX = toCanvasX(currentHandles.out.x);
    const outY = toCanvasY(currentHandles.out.y);
    const distOut = Math.sqrt((mouseX - outX) ** 2 + (mouseY - outY) ** 2);

    if (distOut <= HANDLE_RADIUS + 4) {
      setDraggingHandle('out');
      setLocalHandles(currentHandles);
      return;
    }

    // Check if clicking on in handle
    const inX = toCanvasX(currentHandles.in.x);
    const inY = toCanvasY(currentHandles.in.y);
    const distIn = Math.sqrt((mouseX - inX) ** 2 + (mouseY - inY) ** 2);

    if (distIn <= HANDLE_RADIUS + 4) {
      setDraggingHandle('in');
      setLocalHandles(currentHandles);
      return;
    }
  }, [selectedKeyframe, handles, toCanvasX, toCanvasY]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!draggingHandle || !localHandles || !selectedKeyframe) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    let normX = toNormX(mouseX);
    const normY = toNormY(mouseY);

    // Clamp X to [0, 1] for valid bezier curves
    normX = Math.max(0, Math.min(1, normX));

    setLocalHandles((prev) => {
      if (!prev) return prev;
      if (draggingHandle === 'out') {
        return { ...prev, out: { x: normX, y: normY } };
      } else {
        return { ...prev, in: { x: normX, y: normY } };
      }
    });
  }, [draggingHandle, localHandles, selectedKeyframe, toNormX, toNormY]);

  const handleMouseUp = useCallback(() => {
    if (draggingHandle && localHandles && selectedKeyframe && selectedTrack && animationId) {
      // Commit the bezier handles to the store
      setKeyframeBezier(animationId, selectedTrack.id, selectedKeyframe.id, localHandles);
    }
    setDraggingHandle(null);
    setLocalHandles(null);
  }, [draggingHandle, localHandles, selectedKeyframe, selectedTrack, animationId, setKeyframeBezier]);

  const handleMouseLeave = useCallback(() => {
    if (draggingHandle) {
      handleMouseUp();
    }
  }, [draggingHandle, handleMouseUp]);

  // Cursor style based on state
  const getCursor = useCallback(() => {
    if (draggingHandle) return 'grabbing';
    if (selectedKeyframe?.easing === 'custom') return 'default';
    return 'default';
  }, [draggingHandle, selectedKeyframe]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={cn(
        'bg-zinc-900 rounded',
        selectedKeyframe?.easing === 'custom' && 'cursor-pointer'
      )}
      style={{ cursor: getCursor() }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    />
  );
};

export default CurveCanvas;
